import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Fade,
  Container,
  CircularProgress,
  alpha,
  Chip
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import {
  Business as BusinessIcon,
  ArrowForward,
  CheckCircle
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import api from 'Api';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3),
  },

  container: {
    maxWidth: 800,
    margin: '0 auto',
  },

  paper: {
    padding: theme.spacing(4),
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },

  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },

  icon: {
    fontSize: 64,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },

  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },

  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(2),
  },

  /*
   * Company list gets its own scroll area when there
   * are many companies. Header and action buttons stay visible.
   */
  companyList: {
    maxHeight: 420,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: theme.spacing(1),

    '&::-webkit-scrollbar': {
      width: 8,
    },

    '&::-webkit-scrollbar-track': {
      background: 'rgba(0, 0, 0, 0.05)',
      borderRadius: 4,
    },

    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.25)',
      borderRadius: 4,
    },

    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(0, 0, 0, 0.4)',
    },

    // Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.25) rgba(0, 0, 0, 0.05)',
  },

  companyCard: {
    height: '100%',
    borderRadius: 16,
    transition: 'all 0.3s ease',
    border: '2px solid transparent',

    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
    },
  },

  selectedCard: {
    border: `2px solid ${theme.palette.primary.main}`,
    background: alpha(theme.palette.primary.main, 0.04),
  },

  cardContent: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },

  companyName: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
  },

  companyType: {
    marginBottom: theme.spacing(1),
  },

  checkIcon: {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
  },

  actionArea: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    padding: theme.spacing(2, 0, 0),
  },

  continueBtn: {
    borderRadius: 25,
    padding: theme.spacing(1, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 200,
  },

  cancelBtn: {
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 500,
  },

  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },

  loadingText: {
    marginLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

// Preserve the server-provided company assignments for the HR Analytics switcher.
// This is only a UI fallback: every data request is still authorized by the API.
const persistHrAnalyticsCompanyOptions = (companies) => {
  const userId = String(localStorage.getItem('userId') || '');
  const baseCompanyId = String(localStorage.getItem('companyId') || '');
  if (!userId || !baseCompanyId || !Array.isArray(companies)) return;

  localStorage.setItem('hrAnalyticsAssignedCompanies', JSON.stringify({
    userId,
    baseCompanyId,
    companies
  }));
};

export default function CompanySelector() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        // Try to get from localStorage first
        const storedCompanies = localStorage.getItem('availableCompanies');

        if (storedCompanies) {
          const companiesData = JSON.parse(storedCompanies);
          if (Array.isArray(companiesData)) {
            setCompanies(companiesData);
            persistHrAnalyticsCompanyOptions(companiesData);
            setLoading(false);
            return;
          }
          // Discard malformed cache and recover from the API below.
          localStorage.removeItem('availableCompanies');
        }

        // Fallback: Fetch companies from API
        const parentCompanyId = localStorage.getItem('companyId');
        const userId = localStorage.getItem('userId');

        if (!parentCompanyId || !userId) {
          throw new Error('Missing company or user information');
        }

        const token = localStorage.getItem('token');

        const response = await api.get(
          `/company/${parentCompanyId}/user-assignments/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const companiesData = response.data || [];

        setCompanies(Array.isArray(companiesData) ? companiesData : []);
        localStorage.setItem(
          'availableCompanies',
          JSON.stringify(Array.isArray(companiesData) ? companiesData : [])
        );
        persistHrAnalyticsCompanyOptions(Array.isArray(companiesData) ? companiesData : []);
      } catch (error) {
        console.error('Failed to load companies:', error);

        NotificationManager.error(
          'Failed to load company assignments. Please login again.'
        );

        setTimeout(() => history.push('/signin'), 2000);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [history]);

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
  };

  const handleConfirm = () => {
    if (!selectedCompany) {
      NotificationManager.warning(
        'Please select a company to continue.'
      );
      return;
    }

    const companyId = selectedCompany.companyId || selectedCompany.CompanyId;
    const companyName = selectedCompany.companyName || selectedCompany.CompanyName || 'Company';
    if (!companyId) {
      NotificationManager.error('The selected company has no valid ID. Please select a company again.');
      return;
    }

    // Keep the complete assignment list after the chooser clears its temporary cache.
    // HR Analytics uses it if its separate company-options endpoint is unavailable.
    persistHrAnalyticsCompanyOptions(companies);

    // Keep both the general app workspace and the HR Analytics request scope in sync.
    localStorage.setItem('selectedCompanyId', String(companyId));
    localStorage.setItem('hrAnalyticsSelectedCompanyId', String(companyId));
    localStorage.setItem('selectedCompanyName', String(companyName));

    // Clear dashboard data from the previously selected workspace.
    dispatch({ type: 'HR_WORKSPACE_RESET' });

    // Cleanup
    localStorage.removeItem('availableCompanies');
    localStorage.removeItem('postLoginChooseCompany');

    NotificationManager.success(
      `Welcome to ${companyName}!`
    );

    // Route to dashboard based on access level
    const access = localStorage.getItem('access') || '';

    routeToDashboard(access, history);
  };

  const routeToDashboard = (access, history) => {
    const normalizedAccess = (access || '')
      .toString()
      .toLowerCase();

    if (normalizedAccess === 'admin') {
      history.push('/app/dashboard/ecommerce');
    } else if (
      normalizedAccess === 'ceo' ||
      normalizedAccess === 'hr'
    ) {
      history.push('/app/crm/dashboard');
    } else if (
      normalizedAccess === 'superuser' ||
      normalizedAccess === 'super_user' ||
      normalizedAccess === 'super-user'
    ) {
      history.push('/app/dashboard/hr-analytics/overview');
    } else if (
      normalizedAccess === 'team manager' ||
      normalizedAccess === 'teammanager'
    ) {
      history.push('/app/dashboard/saas');
    } else {
      history.push('/app/dashboard/ecommerce');
    }
  };

  const handleCancel = () => {
    localStorage.removeItem('availableCompanies');
    localStorage.removeItem('postLoginChooseCompany');
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('selectedCompanyName');
    localStorage.removeItem('hrAnalyticsSelectedCompanyId');
    localStorage.removeItem('hrAnalyticsAssignedCompanies');
    dispatch({ type: 'HR_WORKSPACE_RESET' });

    history.push('/signin');
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <Container className={classes.container}>
          <Paper className={classes.paper}>
            <div className={classes.loadingContainer}>
              <CircularProgress size={40} />

              <Typography
                variant="h6"
                className={classes.loadingText}
              >
                Loading company assignments...
              </Typography>
            </div>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container className={classes.container}>
        <Fade in={true} timeout={800}>
          <Paper className={classes.paper}>
            <div className={classes.header}>
              {/* <CorporateFareIcon className={classes.icon} /> */}

              <Typography
                variant="h4"
                className={classes.title}
              >
                Select Company
              </Typography>

              <Typography
                variant="h6"
                className={classes.subtitle}
              >
                Choose the company you want to manage in this session
              </Typography>

              <Typography
                variant="body2"
                color="textSecondary"
              >
                You have access to multiple companies. Please select one to continue.
              </Typography>
            </div>

            {/* Scrollable company list */}
            <div className={classes.companyList}>
              <Grid container spacing={3}>
                {companies.map((company) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={company.companyId}
                  >
                    <Card
                      className={`${classes.companyCard} ${
                        selectedCompany?.companyId === company.companyId
                          ? classes.selectedCard
                          : ''
                      }`}
                    >
                      <CardActionArea
                        onClick={() =>
                          handleCompanySelect(company)
                        }
                      >
                        <CardContent
                          className={classes.cardContent}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mb={1}
                          >
                            <BusinessIcon color="primary" />
                          </Box>

                          <Typography
                            variant="h6"
                            className={classes.companyName}
                          >
                            {company.companyName}

                            {selectedCompany?.companyId ===
                              company.companyId && (
                              <CheckCircle
                                className={classes.checkIcon}
                              />
                            )}
                          </Typography>

                          {company.companyType && (
                            <Chip
                              label={company.companyType}
                              size="small"
                              color={
                                company.isParent
                                  ? 'primary'
                                  : 'secondary'
                              }
                              className={classes.companyType}
                            />
                          )}

                          {company.location && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >
                              {company.location}
                            </Typography>
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>

            <Box className={classes.actionArea}>
              <Button
                className={classes.cancelBtn}
                variant="outlined"
                onClick={handleCancel}
              >
                Cancel
              </Button>

              <Button
                className={classes.continueBtn}
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={!selectedCompany}
                endIcon={<ArrowForward />}
              >
                Continue to{' '}
                {selectedCompany?.companyName || 'Dashboard'}
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </div>
  );
}